import type { CurrentUser } from "./CurrentUserProvider";

/**
 * Authorization primitives over the current user's profile.
 *
 * The backend exposes three independent string lists on the current user:
 * - `permissions`: fine-grained access rights evaluated by API voters.
 * - `oidcRoles`: Keycloak realm roles carried on the OIDC token.
 * - `affiliateFeatureTypes`: features enabled for the active affiliate.
 *
 * Each helper is a pure membership check, so it is trivially unit-testable
 * without React and reusable anywhere (route guards, conditional UI, hooks).
 * Identifiers are plain strings on purpose: `permissions` and `oidcRoles` are
 * open-ended, and feature types are kept consistent with them rather than
 * mirroring the backend enum in a second, drift-prone source of truth.
 */

/** Whether the user holds the given fine-grained permission, or every one of a list. */
export function hasPermission(
  user: CurrentUser,
  permission: string | readonly string[],
): boolean {
  return [permission]
    .flat()
    .every((required) => user.permissions.includes(required));
}

/** Whether the active affiliate has the given feature enabled. */
export function hasFeature(user: CurrentUser, feature: string): boolean {
  return user.affiliateFeatureTypes.includes(feature);
}

/** Whether the user has the given OIDC (Keycloak realm) role. */
export function hasOidcRole(user: CurrentUser, role: string): boolean {
  return user.oidcRoles.includes(role);
}

/**
 * The Keycloak realm role held by Rumble staff (as opposed to client
 * administrators). Used to gate surfaces that must stay staff-only even where
 * the corresponding tenant permission can be granted to a client admin.
 */
export const OIDC_ADMIN_ROLE = "ROLE_OIDC_ADMIN";
